import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { bins, formatWeekRange, messages } from '@fruitways/shared';
import { Button, Icon, ListRow } from '@/components';
import { AppFrame } from '@/app/AppFrame';
import { ProfileMenu } from '@/app/ProfileMenu';
import { sum, useCapture } from '@/capture/CaptureContext';
import { submitDraft } from '@/db/estimateActions';
import { useAuth } from '@/hooks/useAuth';
import { useOnline } from '@/hooks/useOnline';
import { useMasterData } from '@/hooks/useMasterData';
import { EstimateTable } from './EstimateTable';
import './capture.css';

/** Step 3 of 3: check the numbers, then submit. Works with no signal. */
export function ReviewScreen() {
  const nav = useNavigate();
  const master = useMasterData();
  const { user } = useAuth();
  const { online } = useOnline();
  const { draft, hasUnsaved } = useCapture();
  const [busy, setBusy] = useState(false);

  if (!draft || !draft.farmId || draft.picked.length === 0)
    return <Navigate to="/capture" replace />;

  const isEdit = Boolean(draft.editingId);
  const farm = master?.farms.find((f) => f.id === draft.farmId);
  const season = master?.seasons.find((s) => s.id === draft.seasonId);
  const lines = draft.picked.map((code) => ({
    varietyCode: code,
    bins: draft.values[code] ?? ([0, 0, 0, 0, 0, 0] as const),
  }));
  const total = draft.picked.reduce((t, c) => t + sum(draft.values[c]), 0);

  async function submit() {
    if (!draft || !user) return;
    setBusy(true);
    try {
      const { outcome, estimate } = await submitDraft(draft, user.id);
      nav('/capture/success', {
        replace: true,
        state: {
          outcome,
          farmName: farm?.name ?? '',
          weekNumber: estimate.weekNumber,
          total,
        },
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <AppFrame
      title={isEdit ? messages.review.editTitle : messages.review.title}
      subtitle={farm ? messages.header.farmWeek(farm.name, draft.weekNumber) : undefined}
      onBack={() => nav('/capture/entry')}
      action={<ProfileMenu unsaved={hasUnsaved} />}
    >
      <div className="fw-content" style={{ paddingBottom: 'var(--space-4)' }}>
        <span className="fw-secondary">
          {isEdit ? messages.review.checkChanges : messages.capture.step3}
        </span>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          <span className="fw-label">{messages.review.section}</span>
          <div className="fw-rows">
            <ListRow title={messages.common.season} meta={season?.label ?? ''} />
            <ListRow title={messages.common.productionUnit} meta={farm?.name ?? ''} />
            <ListRow
              title={messages.common.week}
              meta={messages.common.weekNumberDates(
                draft.weekNumber,
                formatWeekRange(draft.weekStart),
              )}
            />
          </div>
        </div>

        <EstimateTable lines={lines} />

        <div
          className="fw-panel fw-panel--tint"
          style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}
        >
          <span className="fw-label fw-label--primary">{messages.review.totalExpected}</span>
          <span className="fw-title fw-tnum" style={{ color: 'var(--color-primary)' }}>
            {bins(total)}
          </span>
        </div>

        {!online && (
          <span className="fw-inline-note">
            <Icon name="time-outline" size={16} style={{ marginTop: '2px' }} />
            {messages.review.offlineNote}
          </span>
        )}
      </div>

      <div className="fw-actions fw-actions--column">
        <Button onClick={submit} loading={busy}>
          {isEdit ? messages.review.update : messages.review.submit}
        </Button>
        <Button variant="ghost" onClick={() => nav('/capture/entry')}>
          {messages.review.backToEntry}
        </Button>
      </div>
    </AppFrame>
  );
}
